// Localização: frontend/src/components/Financeiro/FormContaPagar.jsx
// Formulário para criar/editar contas a pagar

import React, { useState, useEffect } from 'react';
import { categoriaApi, subcategoriaApi, contaBancariaApi } from '../../api/financeiroApi';
import PrimaryButton from '../Common/PrimaryButton';
import LoadingIndicator from '../Common/LoadingIndicator';
import ParcelasManager from './ParcelasManager';

const FormContaPagar = ({ conta, onSave, onCancel, isLoading, pacienteId, descricaoInicial }) => {
    const [formData, setFormData] = useState({
        descricao: '',
        valor_total: '',
        data_vencimento: '',
        data_competencia: '',
        categoria_id: '',
        subcategoria_id: '',
        conta_bancaria_id: '',
        cliente_fornecedor: '',
        documento: '',
        observacoes: '',
        tipo_lancamento: 'unica',
        lancar_quitada: false,
        status: 'Pendente'
    });

    const [parcelas, setParcelas] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [subcategorias, setSubcategorias] = useState([]);
    const [contasBancarias, setContasBancarias] = useState([]);
    const [isLoadingData, setIsLoadingData] = useState(false);
    const [errors, setErrors] = useState({});

    // Carregar dados iniciais
    useEffect(() => {
        const carregarDados = async () => {
            setIsLoadingData(true);
            try {
                // Carregar categorias do tipo "Despesa"
                const catResponse = await categoriaApi.getAll({ tipo: 'Despesa', ativo: true });
                setCategorias(catResponse.data.data || []);

                // Carregar contas bancárias ativas
                const contasResponse = await contaBancariaApi.getAll({ ativo: true });
                setContasBancarias(contasResponse.data.data || []);
            } catch (error) {
                console.error('Erro ao carregar dados:', error);
            } finally {
                setIsLoadingData(false);
            }
        };

        carregarDados();
    }, []);

    // Preencher formulário se estiver editando
    useEffect(() => {
        if (conta) {
            const dataCompetencia = conta.data_competencia || conta.data_vencimento || '';

            setFormData({
                descricao: conta.descricao || '',
                valor_total: conta.valor_total || '',
                data_vencimento: conta.data_vencimento || '',
                data_competencia: dataCompetencia,
                categoria_id: conta.categoria?.id || '',
                subcategoria_id: conta.subcategoria?.id || '',
                conta_bancaria_id: conta.conta_bancaria?.id || conta.conta_bancaria_id || '',
                cliente_fornecedor: conta.cliente_fornecedor || '',
                documento: conta.documento || '',
                observacoes: conta.observacoes || '',
                tipo_lancamento: conta.tipo_lancamento || 'unica',
                lancar_quitada: conta.status === 'Pago',
                status: conta.status || 'Pendente'
            });

            // Carregar subcategorias da categoria selecionada
            if (conta.categoria?.id) {
                carregarSubcategorias(conta.categoria.id);
            }
        } else {
            // Ao criar nova conta, definir data_competencia como data atual
            const hoje = new Date().toISOString().split('T')[0];
            setFormData(prev => ({ 
                ...prev, 
                data_competencia: hoje,
                // Pré-preencher descrição se fornecida
                descricao: descricaoInicial || prev.descricao
            }));
        }
    }, [conta, descricaoInicial]);

    // Carregar subcategorias quando categoria mudar
    const carregarSubcategorias = async (categoriaId) => {
        if (!categoriaId) {
            setSubcategorias([]);
            setFormData(prev => ({ ...prev, subcategoria_id: '' }));
            return;
        }

        try {
            const response = await subcategoriaApi.getAll({ categoria_id: categoriaId, ativo: true });
            setSubcategorias(response.data.data || []);
        } catch (error) {
            console.error('Erro ao carregar subcategorias:', error);
            setSubcategorias([]);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newValue = type === 'checkbox' ? checked : value;

        setFormData(prev => ({ ...prev, [name]: newValue }));

        // Limpar erro do campo
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }

        // Se mudou a categoria, carregar subcategorias
        if (name === 'categoria_id') {
            carregarSubcategorias(value);
            setFormData(prev => ({ ...prev, subcategoria_id: '' }));
        }

        // Se mudou tipo_lancamento para não-parcelada, limpar parcelas
        if (name === 'tipo_lancamento' && value !== 'parcelada') {
            setParcelas([]);
        }

        // Se marcou lancar_quitada, data_competencia = data_vencimento
        if (name === 'lancar_quitada' && checked && formData.data_vencimento) {
            setFormData(prev => ({ ...prev, data_competencia: prev.data_vencimento }));
        }
    };

    const validate = () => {
        const newErrors = {};

        if (!formData.descricao.trim()) {
            newErrors.descricao = 'Descrição é obrigatória';
        }

        if (!formData.valor_total || parseFloat(formData.valor_total) <= 0) {
            newErrors.valor_total = 'Valor total deve ser maior que zero';
        }

        if (!formData.data_vencimento) {
            newErrors.data_vencimento = 'Data de vencimento é obrigatória';
        }

        if (!formData.data_competencia) {
            newErrors.data_competencia = 'Data de competência é obrigatória';
        }

        if (!formData.categoria_id) {
            newErrors.categoria_id = 'Categoria é obrigatória';
        }

        if (formData.lancar_quitada && !formData.conta_bancaria_id) {
            newErrors.conta_bancaria_id = 'Conta bancária é obrigatória para lançamento quitado';
        }

        if (formData.tipo_lancamento === 'parcelada') {
            if (parcelas.length === 0) {
                newErrors.parcelas = 'Configure as parcelas antes de salvar';
            } else {
                // Validar soma das parcelas
                const soma = parcelas.reduce((acc, p) => acc + parseFloat(p.valor_parcela || 0), 0);
                const diferenca = Math.abs(parseFloat(formData.valor_total) - soma);
                if (diferenca > 0.01) {
                    newErrors.parcelas = 'A soma das parcelas deve ser igual ao valor total';
                }

                // Validar cada parcela
                for (let i = 0; i < parcelas.length; i++) {
                    if (!parcelas[i].data_vencimento) {
                        newErrors.parcelas = `Parcela ${i + 1} sem data de vencimento`;
                        break;
                    }
                    if (!parcelas[i].valor_parcela || parseFloat(parcelas[i].valor_parcela) <= 0) {
                        newErrors.parcelas = `Parcela ${i + 1} com valor inválido`;
                        break;
                    }
                }
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!validate()) {
            return;
        }

        const dataToSave = {
            descricao: formData.descricao,
            valor_total: parseFloat(formData.valor_total),
            data_vencimento: formData.data_vencimento,
            data_competencia: formData.data_competencia,
            categoria_id: formData.categoria_id,
            subcategoria_id: formData.subcategoria_id || null,
            conta_bancaria_id: formData.conta_bancaria_id || null,
            cliente_fornecedor: formData.cliente_fornecedor || null,
            documento: formData.documento || null,
            observacoes: formData.observacoes || null,
            tipo_lancamento: formData.tipo_lancamento,
            lancar_quitada: formData.lancar_quitada
        };

        // Incluir paciente_id se fornecido (vindo do contexto do prontuário)
        if (pacienteId) {
            dataToSave.paciente_id = pacienteId;
        }

        // Adicionar parcelas se for parcelada
        if (formData.tipo_lancamento === 'parcelada') {
            dataToSave.parcelas = parcelas;
        }

        // Adicionar status apenas se estiver editando (permite reativar contas canceladas)
        if (conta && formData.status) {
            dataToSave.status = formData.status;
        }

        onSave(dataToSave);
    };

    if (isLoadingData) {
        return (
            <div className="flex justify-center items-center py-8">
                <LoadingIndicator />
            </div>
        );
    }

    const mostrarParcelas = formData.tipo_lancamento === 'parcelada';
    const mostrarContaBancaria = formData.tipo_lancamento !== 'parcelada';

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* Descrição */}
            <div>
                <label htmlFor="descricao" className="block text-sm font-medium text-gray-700 mb-1">
                    Descrição <span className="text-red-500">*</span>
                </label>
                <input
                    type="text"
                    id="descricao"
                    name="descricao"
                    value={formData.descricao}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.descricao ? 'border-red-500' : 'border-gray-300'
                        }`}
                    placeholder="Ex: Pagamento de fornecedor"
                />
                {errors.descricao && (
                    <p className="mt-1 text-sm text-red-600">{errors.descricao}</p>
                )}
            </div>

            {/* Valor Total e Data de Vencimento */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="valor_total" className="block text-sm font-medium text-gray-700 mb-1">
                        Valor Total <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="number"
                        id="valor_total"
                        name="valor_total"
                        value={formData.valor_total}
                        onChange={handleChange}
                        step="0.01"
                        min="0.01"
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.valor_total ? 'border-red-500' : 'border-gray-300'
                            }`}
                        placeholder="0.00"
                    />
                    {errors.valor_total && (
                        <p className="mt-1 text-sm text-red-600">{errors.valor_total}</p>
                    )}
                </div>

                <div>
                    <label htmlFor="data_vencimento" className="block text-sm font-medium text-gray-700 mb-1">
                        Data de Vencimento <span className="text-red-500">*</span>
                    </label>
                    <input
                        type="date"
                        id="data_vencimento"
                        name="data_vencimento"
                        value={formData.data_vencimento}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.data_vencimento ? 'border-red-500' : 'border-gray-300'
                            }`}
                    />
                    {errors.data_vencimento && (
                        <p className="mt-1 text-sm text-red-600">{errors.data_vencimento}</p>
                    )}
                </div>
            </div>

            {/* Data de Competência */}
            <div>
                <label htmlFor="data_competencia" className="block text-sm font-medium text-gray-700 mb-1">
                    Data de Competência <span className="text-red-500">*</span>
                </label>
                <input
                    type="date"
                    id="data_competencia"
                    name="data_competencia"
                    value={formData.data_competencia}
                    onChange={handleChange}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.data_competencia ? 'border-red-500' : 'border-gray-300'
                        }`}
                />
                {errors.data_competencia && (
                    <p className="mt-1 text-sm text-red-600">{errors.data_competencia}</p>
                )}
                <p className="mt-1 text-xs text-gray-500">
                    Data para cálculo do DRE (Demonstração do Resultado do Exercício)
                </p>
            </div>

            {/* Categoria e Subcategoria */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="categoria_id" className="block text-sm font-medium text-gray-700 mb-1">
                        Categoria <span className="text-red-500">*</span>
                    </label>
                    <select
                        id="categoria_id"
                        name="categoria_id"
                        value={formData.categoria_id}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.categoria_id ? 'border-red-500' : 'border-gray-300'
                            }`}
                    >
                        <option value="">Selecione uma categoria</option>
                        {categorias.map((cat) => (
                            <option key={cat.id} value={cat.id}>
                                {cat.nome}
                            </option>
                        ))}
                    </select>
                    {errors.categoria_id && (
                        <p className="mt-1 text-sm text-red-600">{errors.categoria_id}</p>
                    )}
                </div>

                <div>
                    <label htmlFor="subcategoria_id" className="block text-sm font-medium text-gray-700 mb-1">
                        Subcategoria
                    </label>
                    <select
                        id="subcategoria_id"
                        name="subcategoria_id"
                        value={formData.subcategoria_id}
                        onChange={handleChange}
                        disabled={!formData.categoria_id || subcategorias.length === 0}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                        <option value="">Selecione uma subcategoria</option>
                        {subcategorias.map((sub) => (
                            <option key={sub.id} value={sub.id}>
                                {sub.nome}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Tipo de Lançamento */}
            <div>
                <label htmlFor="tipo_lancamento" className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo de Lançamento <span className="text-red-500">*</span>
                </label>
                <select
                    id="tipo_lancamento"
                    name="tipo_lancamento"
                    value={formData.tipo_lancamento}
                    onChange={handleChange}
                    disabled={!!conta}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                >
                    <option value="unica">Única</option>
                    <option value="parcelada">Parcelada</option>
                    <option value="recorrente_fixo">Recorrente Fixo</option>
                    <option value="recorrente_variavel">Recorrente Variável</option>
                </select>
                {conta && (
                    <p className="mt-1 text-xs text-gray-500">
                        Tipo de lançamento não pode ser alterado após criação
                    </p>
                )}
            </div>

            {/* Parcelas (apenas se tipo = parcelada) */}
            {mostrarParcelas && (
                <div>
                    <ParcelasManager
                        valorTotal={formData.valor_total}
                        parcelas={parcelas}
                        onChange={setParcelas}
                        disabled={!!conta}
                    />
                    {errors.parcelas && (
                        <p className="mt-2 text-sm text-red-600">{errors.parcelas}</p>
                    )}
                </div>
            )}

            {/* Conta Bancária (apenas se não for parcelada) */}
            {mostrarContaBancaria && (
                <div>
                    <label htmlFor="conta_bancaria_id" className="block text-sm font-medium text-gray-700 mb-1">
                        Conta Bancária {formData.lancar_quitada && <span className="text-red-500">*</span>}
                    </label>
                    <select
                        id="conta_bancaria_id"
                        name="conta_bancaria_id"
                        value={formData.conta_bancaria_id}
                        onChange={handleChange}
                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 ${errors.conta_bancaria_id ? 'border-red-500' : 'border-gray-300'
                            }`}
                        disabled={isLoadingData}
                    >
                        <option value="">Selecione uma conta bancária...</option>
                        {contasBancarias.map((conta) => (
                            <option key={conta.id} value={conta.id}>
                                {conta.nome} - {conta.banco} {conta.agencia ? `(${conta.agencia})` : ''}
                            </option>
                        ))}
                    </select>
                    {errors.conta_bancaria_id && (
                        <p className="mt-1 text-sm text-red-600">{errors.conta_bancaria_id}</p>
                    )}
                </div>
            )}

            {/* Lançar como Quitada */}
            {!conta && (
                <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                    <input
                        type="checkbox"
                        id="lancar_quitada"
                        name="lancar_quitada"
                        checked={formData.lancar_quitada}
                        onChange={handleChange}
                        className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                    />
                    <label htmlFor="lancar_quitada" className="text-sm font-medium text-gray-700 cursor-pointer">
                        Lançar como quitada (já paga)
                    </label>
                </div>
            )}

            {/* Cliente/Fornecedor e Documento */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label htmlFor="cliente_fornecedor" className="block text-sm font-medium text-gray-700 mb-1">
                        Fornecedor
                    </label>
                    <input
                        type="text"
                        id="cliente_fornecedor"
                        name="cliente_fornecedor"
                        value={formData.cliente_fornecedor}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        placeholder="Nome do fornecedor"
                    />
                </div>

                <div>
                    <label htmlFor="documento" className="block text-sm font-medium text-gray-700 mb-1">
                        CPF/CNPJ
                    </label>
                    <input
                        type="text"
                        id="documento"
                        name="documento"
                        value={formData.documento}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        placeholder="000.000.000-00 ou 00.000.000/0000-00"
                    />
                </div>
            </div>

            {/* Status (apenas ao editar) */}
            {conta && (
                <div>
                    <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                    </label>
                    <select
                        id="status"
                        name="status"
                        value={formData.status}
                        onChange={handleChange}
                        disabled={conta.status === 'Pago'}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                        <option value="Pendente">Pendente</option>
                        <option value="Vencido">Vencido</option>
                        <option value="Cancelado">Cancelado</option>
                    </select>
                    <p className="mt-1 text-xs text-gray-500">
                        {conta.status === 'Pago' 
                            ? 'Não é possível alterar o status de uma conta já paga.' 
                            : 'Altere o status para reativar uma conta cancelada.'}
                    </p>
                </div>
            )}

            {/* Observações */}
            <div>
                <label htmlFor="observacoes" className="block text-sm font-medium text-gray-700 mb-1">
                    Observações
                </label>
                <textarea
                    id="observacoes"
                    name="observacoes"
                    value={formData.observacoes}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    placeholder="Observações adicionais..."
                />
            </div>

            {/* Botões */}
            <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                    type="button"
                    onClick={onCancel}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                    Cancelar
                </button>
                <PrimaryButton
                    type="submit"
                    disabled={isLoading}
                    className="px-4 py-2"
                >
                    {isLoading ? 'Salvando...' : conta ? 'Atualizar' : 'Criar'}
                </PrimaryButton>
            </div>
        </form>
    );
};

export default FormContaPagar;
